import { Checkbox, Collapse, DatePicker, Form, Input, InputNumber, Radio, Select, Button } from "antd";
import dayjs from "dayjs"
import { FileUploadField } from "./fileUploadField";
import { VerificationStatus } from "./constant";
import { t } from "i18next";
import React, { Fragment } from "react";
import { extractLocalizedValue } from ".";
import { MobileCodeList } from "../../../../config/mobileCodeList";
import PhoneNumberInput from "./phoneNumberInput";
import CollapsePanel from "antd/es/collapse/CollapsePanel";
import { QuestionnaireField } from "./questionnaireField";

export const renderFormFields = (
    t,
    form,
    fieldsOrDocuments = [],
    verificationStatus,
    handleAutoSave,
    parentPath = [],
    layoutOptions = {},
) => {
    const isDocument = (item) => Array.isArray(item?.steps);

    const readOnly = verificationStatus === VerificationStatus.PENDING;

    const getScopedName = (parentPath, childId) => {
        return parentPath ? [...parentPath, childId] : [childId];
    }

    const renderField = (field, isRequired, fieldPath) => {
        const hasExpired = field.hasExpired;
        const isDisabled = field.isEnabled === false;

        // localized labels & placeholders
        const label = extractLocalizedValue(field.displayName) || field.name;
        const placeholder = extractLocalizedValue(field.description);
        const rules = buildValidationRules(field, isRequired)

        const commonProps = {
            name: fieldPath, // array of string IDs
            label: label,
            rules: rules,
        };

        const sharedInputProps = {
            //disabled: isDisabled,
            //placeholder: placeholder,
            disabled: readOnly,
            ...field.props, // allow external props overrides (e.g., minLength, maxLength, custom onChange)
        };

        let inputElement = null;

        switch (field.type) {
            case "text":
                inputElement = <Input {...sharedInputProps} />;
                break;

            case "textarea":
                inputElement =
                    <Input.TextArea
                        autoSize={{ minRows: 3, maxRows: 6 }}
                        style={{ resize: "vertical" }}
                        {...sharedInputProps}
                    />;
                break;

            case "integer":
                inputElement = (
                    <InputNumber
                        step={1}
                        precision={0}
                        className={"w-full"}
                        {...sharedInputProps}
                    />
                );
                break;

            case "double":
            case "currency":
                inputElement = (
                    <InputNumber
                        step={0.01}
                        min={field.type === "currency" ? 0 : undefined}
                        className={"w-full"}
                        prefix={field.type === "currency" ? "$" : undefined}
                        {...sharedInputProps}
                    />
                );
                break;

            case "date":
                inputElement = (
                    <DatePicker
                        style={{ width: "100%" }}
                        format="YYYY-MM-DD"
                        allowClear={false}
                        showToday={false}
                        defaultPickerValue={dayjs().subtract(18, "years")}
                        inputReadOnly
                        disabled={readOnly}
                        {...sharedInputProps}
                    />
                );
                break;

            case "time":
                inputElement = (
                    <DatePicker.TimePicker
                        style={{ width: "100%" }}
                        getValueProps={(value) => ({
                            value: value ? dayjs(value, "HH:mm") : null,
                        })}
                        inputReadOnly
                        disabled={readOnly}
                        {...sharedInputProps}
                    />
                );
                break;

            case "datetime":
                inputElement = (
                    <DatePicker
                        showTime
                        style={{ width: "100%" }}
                        format="YYYY-MM-DD HH:mm"
                        inputReadOnly
                        disabled={readOnly}
                        {...sharedInputProps}
                    />
                );
                break;

            case "checkbox":
                inputElement = (
                    <Checkbox.Group
                        options={(field.optionValues || []).map((opt) => ({
                            label: t(extractLocalizedValue(opt?.displayName ?? opt)),
                            value: opt?.value ?? opt,
                        }))}
                        disabled={readOnly}
                        {...sharedInputProps}
                    />
                );
                break;

            case "radio_button":
                inputElement = (
                    <Radio.Group
                        disabled={readOnly}
                        {...sharedInputProps}
                    >
                        {(field.optionValues || []).map((opt) => (
                            <Radio key={opt?.value ?? opt} value={opt?.value ?? opt}>
                                {t(extractLocalizedValue(opt?.displayName ?? opt))}
                            </Radio>
                        ))}
                    </Radio.Group>
                );
                break;

            case "select":
                inputElement = (
                    <Select
                        {...sharedInputProps}
                        showSearch={(field.optionValues?.length ?? 0) > 5}
                        options={(field.optionValues || []).map((opt) => ({
                            label: t(extractLocalizedValue(opt?.displayName ?? opt)),
                            value: opt?.value ?? opt,
                        }))}
                        optionFilterProp={"label"}
                        disabled={readOnly}
                    />
                );
                break;

            case "multi_select":
                inputElement = (
                    <Select
                        mode="multiple"
                        allowClear
                        {...sharedInputProps}
                        options={(field.optionValues || []).map((opt) => ({
                            label: t(extractLocalizedValue(opt?.displayName ?? opt)),
                            value: opt?.value ?? opt,
                        }))}
                        optionFilterProp={"label"}
                        disabled={readOnly}
                    />
                );
                break;

            case "phone_number":

                inputElement = (
                    <Form.Item
                        label={label}
                        //rules={rules}
                        required={isRequired}
                    >
                        <PhoneNumberInput
                            form={form}
                            field={field}
                            fieldPath={fieldPath}
                            isRequired={isRequired}
                            label={label}
                            readOnly={readOnly}
                        />
                    </Form.Item>

                );

                return (
                    <div key={fieldPath.join("_")} style={{ marginBottom: 16 }}>
                        {inputElement}
                    </div>
                );

            case "file":
                return (
                    <div key={fieldPath.join("_")} style={{ marginBottom: 16 }}>
                        <Form.Item
                            name={fieldPath}
                            label={label}
                            //valuePropName="fileList"
                            // getValueFromEvent={(e) => {
                            //     const file = Array.isArray(e) ? e[0] : e?.fileList?.[0];
                            //     return file?.response || file?.url || null;
                            // }}
                            rules={
                                isRequired
                                    ? [
                                        {
                                            required: true,
                                            message: t("errorMsg.required.requiredField", {
                                                field: label,
                                            }),
                                        },
                                    ]
                                    : []
                            }
                            style={{
                                ...layoutOptions.fieldStyle,
                            }}
                        >
                            <FileUploadField
                                form={form}
                                field={field}
                                fieldPath={fieldPath}
                                sharedInputProps={sharedInputProps}
                                handleAutoSave={handleAutoSave}
                            />
                        </Form.Item>
                    </div>
                );

            case "questionnaire":
                inputElement = (
                    <>
                        <QuestionnaireField
                            formSettingId={field.formSettingId}
                            fieldPath={fieldPath}
                            form={form}
                            handleAutoSave={handleAutoSave}
                            layout={layoutOptions?.layout}
                        />
                        <Form.Item name={fieldPath} hidden>
                            <Input />
                        </Form.Item>
                    </>
                );
                break;

            default:
                inputElement = null;
                break;
        }

        if (!inputElement) return null;

        return (
            <div key={fieldPath.join("_")} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                {
                    field.type === "questionnaire" ? inputElement : (
                        <>
                            <Form.Item
                                {...commonProps}
                                style={{ flex: 1 }}
                            >
                                {inputElement}
                            </Form.Item>

                            {hasExpired && (
                                <Form.Item
                                    name={["expiredAt", ...fieldPath]}
                                    label={t("userKycField.expired_date")}
                                    style={{ flex: 1 }}
                                    rules={[
                                        {
                                            required: true,
                                            message: t("errorMsg.required.pleaseFillIn", {
                                                field: t("userKycField.expired_date")
                                            })
                                        }
                                    ]}
                                >
                                    <DatePicker
                                        style={{ width: "100%" }}
                                        inputReadOnly
                                        disabledDate={(current) => {
                                            return current && current < dayjs().endOf("day");
                                        }}
                                        disabled={readOnly}
                                    />
                                </Form.Item>
                            )}
                        </>
                    )
                }
            </div>
        );
    }

    return fieldsOrDocuments?.map((item) => {
        if (isDocument(item)) {
            const documentPath = getScopedName(parentPath, item._id);

            return (
                <Collapse ghost expandIcon={() => <></>} key={documentPath.join("_")} className={"document-section"}>
                    <CollapsePanel
                        key={documentPath.join("_")}
                        header={
                            <>
                                {item?.isRequired && <span className={"danger-text me-1 text-md"}>*</span>}
                                {extractLocalizedValue(item.displayName)}
                            </>
                        }
                    >
                        {item.steps?.map((step) => {
                            const stepPath = getScopedName(documentPath, step.sourceId || step._id);

                            return (
                                <Fragment key={stepPath.join("_")}>
                                    {isDocument(step.sourceDoc) ? renderFormFields(t, form, [step.sourceDoc], verificationStatus, handleAutoSave, documentPath, layoutOptions) : renderField(step.sourceDoc?.type === "questionnaire" ? { ...step.sourceDoc, formSettingId: step.questionnaireId } : step.sourceDoc, step.isRequired, stepPath)}
                                </Fragment>
                            )
                        })}
                    </CollapsePanel>
                </Collapse>
            );
        }

        const fieldPath = getScopedName(parentPath, item._id);

        return renderField(item, item.isRequired, fieldPath);
    })
}

// Parse a stored date/time value tolerant of format drift between our saved data
// (YYYY-MM-DD etc.) and backend/legacy ISO strings: try the exact format first
// (strict — needs customParseFormat), then fall back to lenient parsing.
const parseDateLike = (value, format) => {
    if (!value) return null;
    const strict = dayjs(value, format, true);
    if (strict.isValid()) return strict;
    const loose = dayjs(value);
    return loose.isValid() ? loose : null;
};

export function initializeDocumentForm(currentDoc, selectedDocument, parentPath = []) {
    const initialValues = {};
    if (!currentDoc?.steps || !selectedDocument?.steps) return initialValues;

    for (const step of selectedDocument.steps) {
        const field = step.sourceDoc;
        const fieldId = field?._id;

        if (!fieldId) continue;

        const safeParentPath = Array.isArray(parentPath) ? parentPath : [parentPath];
        const fieldPath = [...safeParentPath, fieldId];
        const currentStep = currentDoc.steps.find(s => s.sourceId === fieldId);
        let value = currentStep?.value;
        const expiredAt = currentStep?.expiredAt;

        // nested document_setting: recurse to seed the inner leaf values (mirror buildSteps)
        const isNested = (step.sourceType && step.sourceType.toString().toLowerCase() === "document_setting")
            || (Array.isArray(step.steps) && step.steps.length > 0)
            || (Array.isArray(field.steps) && field.steps.length > 0);

        if (isNested) {
            const nestedValues = initializeDocumentForm(
                { steps: currentStep?.value?.steps || [] },
                { steps: step.steps || field.steps || [] },
                fieldPath
            );
            deepMerge(initialValues, nestedValues);
            continue;
        }

        switch (field.type) {
            case "date":
                value = parseDateLike(value, "YYYY-MM-DD");
                break;
            case "datetime":
                value = parseDateLike(value, "YYYY-MM-DD HH:mm:ss");
                break;
            case "time":
                value = parseDateLike(value, "HH:mm:ss");
                break;
            case "checkbox":
                //value = !!value;
                break;
            case "multi_select":
                value = Array.isArray(value) ? value.filter(Boolean) : value == null ? [] : [value];
                break;
            case "phone_number":
                if (typeof value === "string" && value.trim()) {
                    const trimmed = value.trim();

                    // find a matching country code from your list (longest match first)
                    const matchCountry = [...MobileCodeList]
                        .sort((a, b) => b.value.length - a.value.length) // prioritize longer codes like +358 over +35
                        .find((c) => trimmed.startsWith(c.value));

                    if (matchCountry) {
                        value = {
                            countryCode: matchCountry.value,
                            phoneNumber: trimmed.slice(matchCountry.value.length).trim(),
                        };
                    } else {
                        value = { countryCode: "", phoneNumber: trimmed };
                    }
                } else if (value && typeof value === "object") {
                    value = {
                        countryCode: value.countryCode || "",
                        phoneNumber: value.phoneNumber || "",
                    };
                } else {
                    value = { countryCode: "", phoneNumber: "" };
                }
                break;
            default:
                //if (value == null) value = "";
                break;
        }

        setValueByPath(initialValues, fieldPath, value);
        if (expiredAt) {
            setValueByPath(initialValues, ["expiredAt", ...fieldPath], dayjs(expiredAt))
        }
    }

    return initialValues;
}

function deepMerge(target, source) {
    for (const key of Object.keys(source || {})) {
        const val = source[key];
        if (val && typeof val === "object" && !Array.isArray(val) && !dayjs.isDayjs(val)) {
            if (!target[key] || typeof target[key] !== "object") target[key] = {};
            deepMerge(target[key], val);
        } else {
            target[key] = val;
        }
    }
    return target;
}

function setValueByPath(obj, pathArray, value) {
    const path = Array.isArray(pathArray) ? [...pathArray] : [pathArray];
    const lastKey = path.pop();
    const target = path.reduce((curr, key) => {
        if (!curr[key] || typeof curr[key] !== "object") curr[key] = {};
        return curr[key];
    }, obj);
    target[lastKey] = value;
}

function getValueByPath(obj, pathArray) {
    return pathArray.reduce((acc, key) => acc?.[key], obj);
}


const buildValidationRules = (field, isRequired) => {
    const rules = [];
    const label = extractLocalizedValue(field.displayName) || field.name;

    if (isRequired) {
        rules.push({
            required: true,
            message: t("errorMsg.required.pleaseFillIn", { field: label }),
        });
    }

    // Age validation for "date of birth"
    if (field?.name?.toLowerCase()?.replace(/\s+/g, "").includes("dateofbirth")) {
        rules.push({
            validator: (_, value) => {
                if (!value) return Promise.resolve();

                const age = dayjs().diff(dayjs(value), "year");

                if (age < 18) {
                    return Promise.reject(new Error(t("admin.err.user_must_at_least_18_year_old")));
                }

                return Promise.resolve();
            },
        });
    }

    // Numeric validation (integer, double, currency)
    if (["integer", "double", "currency"].includes(field.type)) {
        rules.push({
            validator: (_, value) => {
                if (value === undefined || value === null || value === "") return Promise.resolve();

                const num = Number(value);
                if (isNaN(num)) {
                    return Promise.reject(new Error(t("errorMsg.invalidNumber")));
                }

                if (field.type === "integer" && !Number.isInteger(num)) {
                    return Promise.reject(new Error(t("errorMsg.invalidInteger")));
                }

                return Promise.resolve();
            },
        });
    }

    return rules;
}